import React, {useEffect} from 'react';
import {Redirect, Route, Switch, withRouter} from "react-router-dom";
import {useBootstrap, useEntities, useFilters, useProgress, useRenderer} from "@atttomyx/shared-hooks";
import {usePushEvent} from "@atttomyx/react-hooks";
import {
    AboutPage,
    ChangePasswordPage,
    ForgotPasswordPage,
    HomePage,
    LoginPage,
    NotFoundPage,
    ProfilePage,
    PublicHeader,
    Splash,
    WelcomePage
} from "@atttomyx/react-components";
import {Footer} from "./components/nav";
import {
    AccountsPage,
    AddAccountPage,
    AddAppPage,
    AddUserPage,
    AdsPage,
    AggregatePage,
    AppsPage,
    CategoriesPage,
    DictionaryPage,
    EditAccountPage,
    EditAppPage,
    EditCategoryPage,
    EditTypePage,
    EditUserPage,
    OAuthPage,
    RobotsPage,
    UsersPage
} from "./components/pages";
import {UserSettingsForm} from "./components/forms";
import {
    appService,
    authService,
    internalAccountService,
    internalAdService,
    internalAppService,
    internalCategoryService,
    internalDictionaryService,
    internalLoginService,
    internalRobotService,
    internalTypeService,
    internalUserService,
    myAccountService,
    notificationService,
    oauthService,
    profileService
} from "./services";
import {sorting} from "@atttomyx/shared-utils";
import {branding as brandingUtils, firebase as firebaseUtils, router} from "@atttomyx/react-utils";
import {dictionaryUtils, loginUtils, userUtils} from "./utils";
import {themes, time} from "@atttomyx/shared-constants";
import {
    APP_NAME,
    APP_TAG_LINE,
    FIREBASE_CONFIG,
    PAGE_ABOUT,
    PAGE_ACCOUNT,
    PAGE_ACCOUNTS,
    PAGE_ADS,
    PAGE_AGGREGATES,
    PAGE_APP,
    PAGE_APPS,
    PAGE_CATEGORIES,
    PAGE_CATEGORY,
    PAGE_DICTIONARY,
    PAGE_HOME,
    PAGE_LOGIN,
    PAGE_OAUTH,
    PAGE_PASSWORD,
    PAGE_RECOVERY,
    PAGE_ROBOTS,
    PAGE_SETTINGS,
    PAGE_TYPE,
    PAGE_TYPES,
    PAGE_USER,
    PAGE_USERS,
    PAGE_WELCOME,
    PRIVATE_PAGES_EXACT,
    PRIVATE_PAGES_STARTS_WITH,
    PUBLIC_PAGES_EXACT,
    PUBLIC_PAGES_STARTS_WITH,
    ROLE_TO_HOME_PAGE
} from "./constants";
import {
    ACCOUNT_FILTERS_DATA,
    APP_FILTERS,
    CATEGORY_FILTERS,
    getAccountFilters,
    getAdFilters,
    getAggregateData,
    getAggregateFilters,
    getRobotFilters,
    TYPE_FILTERS,
    USER_FILTERS,
    WORD_FILTERS
} from "./filters";
import icon from "./icon.png";
import TypesPage from "./components/pages/typesPage";
import AddTypePage from "./components/pages/addTypePage";

const App = props => {
    const {dimensions, snackbar, history, onThemeChange} = props;

    const apps = useEntities(internalAppService.listApps, snackbar.setError, "apps", sorting.sortByTitle);
    const accounts = useEntities(internalAccountService.listAccounts, snackbar.setError, "accounts", sorting.sortByName);
    const types = useEntities(internalTypeService.listTypes, snackbar.setError, "types", sorting.sortByTitle);
    const users = useEntities(internalUserService.listUsers, snackbar.setError, "users", userUtils.sortByNameAndAlias);
    const robots = useEntities(internalRobotService.listRobots, snackbar.setError, "robots", sorting.sortByName);
    const logins = useEntities(internalLoginService.listLogins, snackbar.setError, "logins", sorting.sortByUpdatedDesc);
    const aggregates = useEntities(internalLoginService.listAggregates, snackbar.setError, "aggregates", loginUtils.sortByYearWeek);
    const ads = useEntities(internalAdService.listAds, snackbar.setError, "ads", sorting.sortByUpdatedDesc);
    const categories = useEntities(internalCategoryService.listCategories, snackbar.setError, "categories", sorting.sortByTitle);
    const words = useEntities(internalDictionaryService.listWords, snackbar.setError, "words", dictionaryUtils.sortByWord);
    const integrations = useEntities(oauthService.listIntegrations, snackbar.setError, "integrations", sorting.sortByTitle);

    const appRenderer = useRenderer(apps, (app) => app.title);
    const accountRenderer = useRenderer(accounts, (account) => account.name);
    const typeRenderer = useRenderer(types, (type) => type.title);

    const appFilters = useFilters(APP_FILTERS);
    const accountFilters = useFilters(getAccountFilters(appRenderer), ACCOUNT_FILTERS_DATA);
    const typeFilters = useFilters(TYPE_FILTERS);
    const userFilters = useFilters(USER_FILTERS);
    const robotFilters = useFilters(getRobotFilters(appRenderer));
    const adFilters = useFilters(getAdFilters(appRenderer));
    const categoryFilters = useFilters(CATEGORY_FILTERS);
    const wordFilters = useFilters(WORD_FILTERS);
    const aggregateFilters = useFilters(getAggregateFilters(appRenderer), getAggregateData());

    const onLogout = () => {
        authService.cancelAllRequests("logged out");
        authService.clearAuthToken();

        progress.clear();
        bootstrap.clear();
        apps.clear();
        accounts.clear();
        types.clear();
        users.clear();
        robots.clear();
        logins.clear();
        aggregates.clear();
        ads.clear();
        categories.clear();
        words.clear();
        integrations.clear();

        appFilters.clear();
        accountFilters.clear();
        userFilters.clear();
        robotFilters.clear();
        adFilters.clear();
        categoryFilters.clear();
        wordFilters.clear();
        aggregateFilters.clear();

        router.redirectToInitialPage(history, PUBLIC_PAGES_EXACT, PUBLIC_PAGES_STARTS_WITH, PAGE_LOGIN);
    };

    const bootstrapSuccess = () => {
        if (bootstrap.user.temporary) {
            router.redirectTo(history, PAGE_PASSWORD);

        } else {
            router.redirectToInitialPage(history, PRIVATE_PAGES_EXACT, PRIVATE_PAGES_STARTS_WITH, PAGE_HOME);
        }

        if (bootstrap.user.roles.apps || bootstrap.user.roles.accounts || bootstrap.user.roles.users || bootstrap.user.roles.robots) {
            apps.refresh();

        } else {
            apps.empty();
        }

        if (bootstrap.user.roles.accounts || bootstrap.user.roles.users) {
            accounts.refresh();

        } else {
            accounts.empty();
        }

        if (bootstrap.user.roles.users) {
            types.refresh();
            users.refresh();
            logins.refresh();
            aggregates.refresh();

        } else {
            types.empty();
            users.empty();
            logins.empty();
            aggregates.empty();
        }

        if (bootstrap.user.roles.robots) {
            robots.refresh();

        } else {
            robots.empty();
        }

        if (bootstrap.user.roles.ads) {
            ads.refresh();

        } else {
            ads.empty();
        }

        if (bootstrap.user.roles.reveal) {
            categories.refresh();

        } else {
            categories.empty();
        }

        if (bootstrap.user.roles.words) {
            words.refresh();

        } else {
            words.empty();
        }

        integrations.refresh();

        authService.recordLoginIfNecessary(snackbar.setError);
    };

    const bootstrapFailure = (err) => {
        snackbar.setError(err);
        onLogout();
    };

    const bootstrap = useBootstrap(authService, myAccountService, profileService, notificationService,
        bootstrapSuccess, bootstrapFailure);

    const progress = useProgress(bootstrap,
        [apps, accounts, types, users, robots, logins, aggregates, ads, categories, words, integrations],
        [],
        0,
        time.MILLISECONDS_IN_SECOND);

    const event = usePushEvent(bootstrap);

    const onLogin = (accounts) => {
        progress.refresh();
        bootstrap.refresh(accounts);
    };

    useEffect(() => {
        if (event && event.type) {
            const type = event.type;
            const data = event.payload || {};
            const actor = data.actor || {};

            console.table(type, data);

            if (bootstrap.user.id !== actor.userId) {
                // todo: implement these events in accounts-api
                switch (type) {
                    case "app_saved":
                    case "app_deleted":
                        apps.refresh();
                        break;
                    case "account_saved":
                    case "account_deleted":
                        accounts.refresh();
                        break;
                    case "type_saved":
                    case "type_deleted":
                        types.refresh();
                        break;
                    case "user_saved":
                    case "user_deleted":
                        users.refresh();
                        break;
                    case "robot_saved":
                    case "robot_deleted":
                        robots.refresh();
                        break;
                    case "login_saved":
                    case "login_deleted":
                        logins.refresh();
                        break;
                    case "aggregate_saved":
                        aggregates.refresh();
                        break;
                    case "ad_saved":
                    case "ad_deleted":
                        ads.refresh();
                        break;
                    case "category_saved":
                    case "category_deleted":
                        categories.refresh();
                        break;
                    default:
                        break;
                }
            }
        }
    }, [event]);

    useEffect(() => {
        const settings = bootstrap.user ? bootstrap.user.settings || {} : {};
        const theme = settings.theme || themes.DARK;
        const branding = brandingUtils.getBranding(theme);

        onThemeChange(theme, branding);
    }, [bootstrap.user]);

    useEffect(() => {
        appService.ensureOnLatestVersion(false, null);
        authService.setupAxiosInterceptors(onLogout);
        firebaseUtils.initFirebase(FIREBASE_CONFIG);
        dimensions.onResize();

        if (authService.loggedIn()) {
            onLogin();

        } else {
            onLogout();
        }
    }, []);

    return <div className="app">
        {progress.loading || progress.paused ?
            <Splash
                percent={progress.percent}
            /> : bootstrap.user ?
                <>
                    <div className="content">
                        <Switch>
                            <Route exact path="/">
                                <Redirect to={PAGE_HOME}/>
                            </Route>
                            <Route path={PAGE_HOME}>
                                <HomePage
                                    user={bootstrap.user}
                                    roleToHomePage={ROLE_TO_HOME_PAGE}
                                    onRedirect={(page) => router.redirectTo(history, page)}
                                />
                            </Route>
                            {bootstrap.user.roles.apps ?
                                <Route path={PAGE_APPS}>
                                    <AppsPage
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        apps={apps.entities}
                                        filters={appFilters}
                                        onSave={apps.onEntitySaved}
                                        onDelete={apps.onEntityDeleted}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.apps ?
                                <Route exact path={PAGE_APP}>
                                    <AddAppPage
                                        snackbar={snackbar}
                                        onSave={(app) => {
                                            apps.onEntitySaved(app);
                                            router.redirectTo(history, `${PAGE_APP}/${app.id}`);
                                        }}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.apps ?
                                <Route
                                    path={`${PAGE_APP}/:id`}
                                    render={(props) =>
                                        <EditAppPage
                                            {...props}
                                            snackbar={snackbar}
                                            apps={apps.entities}
                                            filters={appFilters}
                                            onSave={apps.onEntitySaved}
                                            onPrev={(appId) => router.redirectTo(history, `${PAGE_APP}/${appId}`)}
                                            onNext={(appId) => router.redirectTo(history, `${PAGE_APP}/${appId}`)}
                                        />}
                                /> : null}
                            {bootstrap.user.roles.accounts ?
                                <Route path={PAGE_ACCOUNTS}>
                                    <AccountsPage
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        apps={apps.entities}
                                        appRenderer={appRenderer}
                                        accounts={accounts.entities}
                                        filters={accountFilters}
                                        onDelete={accounts.onEntityDeleted}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.accounts ?
                                <Route exact path={PAGE_ACCOUNT}>
                                    <AddAccountPage
                                        snackbar={snackbar}
                                        apps={apps.entities}
                                        appRenderer={appRenderer}
                                        onSaveAccount={(account) => {
                                            accounts.onEntitySaved(account);
                                            router.redirectTo(history, `${PAGE_ACCOUNT}/${account.id}`);
                                        }}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.accounts ?
                                <Route
                                    path={`${PAGE_ACCOUNT}/:id`}
                                    render={(props) =>
                                        <EditAccountPage
                                            {...props}
                                            snackbar={snackbar}
                                            apps={apps.entities}
                                            appRenderer={appRenderer}
                                            accounts={accounts.entities}
                                            filters={accountFilters}
                                            onSaveAccount={accounts.onEntitySaved}
                                            onPrev={(accountId) => router.redirectTo(history, `${PAGE_ACCOUNT}/${accountId}`)}
                                            onNext={(accountId) => router.redirectTo(history, `${PAGE_ACCOUNT}/${accountId}`)}
                                        />}
                                /> : null}
                            {bootstrap.user.roles.users ?
                                <Route path={PAGE_TYPES}>
                                    <TypesPage
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        accounts={accounts.entities}
                                        accountRenderer={accountRenderer}
                                        types={types.entities}
                                        filters={typeFilters}
                                        onDelete={types.onEntityDeleted}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.users ?
                                <Route
                                    path={`${PAGE_TYPE}/:accountId/:typeId`}
                                    render={(props) =>
                                        <EditTypePage
                                            {...props}
                                            snackbar={snackbar}
                                            apps={apps.entities}
                                            accounts={accounts.entities}
                                            types={types.entities}
                                            filters={typeFilters}
                                            onSave={types.onEntitySaved}
                                            onPrev={(accountId, typeId) => router.redirectTo(history, `${PAGE_TYPE}/${accountId}/${typeId}`)}
                                            onNext={(accountId, typeId) => router.redirectTo(history, `${PAGE_TYPE}/${accountId}/${typeId}`)}
                                        />}
                                /> : null}
                            {bootstrap.user.roles.users ?
                                <Route
                                    path={`${PAGE_TYPE}/:accountId`}
                                    render={(props) => <AddTypePage
                                        {...props}
                                        snackbar={snackbar}
                                        apps={apps.entities}
                                        accounts={accounts.entities}
                                        onSave={(accountId, type) => {
                                            types.onEntitySaved(type);
                                            router.redirectTo(history, `${PAGE_TYPE}/${accountId}/${type.id}`);
                                        }}
                                    />}
                                /> : null}
                            {bootstrap.user.roles.users ?
                                <Route path={PAGE_USERS}>
                                    <UsersPage
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        user={bootstrap.user}
                                        apps={apps.entities}
                                        accounts={accounts.entities}
                                        accountRenderer={accountRenderer}
                                        typeRenderer={typeRenderer}
                                        users={users.entities}
                                        logins={logins.entities}
                                        filters={userFilters}
                                        onSave={users.onEntitySaved}
                                        onDelete={users.onEntityDeleted}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.users ?
                                <Route
                                    path={`${PAGE_USER}/:accountId/:userId`}
                                    render={(props) =>
                                        <EditUserPage
                                            {...props}
                                            snackbar={snackbar}
                                            accounts={accounts.entities}
                                            user={bootstrap.user}
                                            users={users.entities}
                                            filters={userFilters}
                                            onSave={users.onEntitySaved}
                                            onPrev={(accountId, userId) => router.redirectTo(history, `${PAGE_USER}/${accountId}/${userId}`)}
                                            onNext={(accountId, userId) => router.redirectTo(history, `${PAGE_USER}/${accountId}/${userId}`)}
                                        />}
                                /> : null}
                            {bootstrap.user.roles.users ?
                                <Route
                                    path={`${PAGE_USER}/:accountId`}
                                    render={(props) => <AddUserPage
                                        {...props}
                                        snackbar={snackbar}
                                        accounts={accounts.entities}
                                        onSave={(accountId, user) => {
                                            users.onEntitySaved(user);
                                            router.redirectTo(history, `${PAGE_USER}/${accountId}/${user.id}`);
                                        }}
                                    />}
                                /> : null}
                            {bootstrap.user.roles.robots ?
                                <Route path={PAGE_ROBOTS}>
                                    <RobotsPage
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        apps={apps.entities}
                                        appRenderer={appRenderer}
                                        robots={robots.entities}
                                        logins={logins.entities}
                                        filters={robotFilters}
                                        onSave={robots.onEntitySaved}
                                        onDelete={robots.onEntityDeleted}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.ads ?
                                <Route path={PAGE_ADS}>
                                    <AdsPage
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        apps={apps.entities}
                                        appRenderer={appRenderer}
                                        ads={ads.entities}
                                        filters={adFilters}
                                        onSave={ads.onEntitySaved}
                                        onDelete={ads.onEntityDeleted}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.reveal ?
                                <Route path={PAGE_CATEGORIES}>
                                    <CategoriesPage
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        categories={categories.entities}
                                        filters={categoryFilters}
                                        onSave={categories.onEntitySaved}
                                        onDelete={categories.onEntityDeleted}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.reveal ?
                                <Route
                                    path={`${PAGE_CATEGORY}/:id`}
                                    render={(props) =>
                                        <EditCategoryPage
                                            {...props}
                                            snackbar={snackbar}
                                            dimensions={dimensions}
                                            categories={categories.entities}
                                            filters={categoryFilters}
                                            onSave={categories.onEntitySaved}
                                            onPrev={(categoryId) => router.redirectTo(history, PAGE_CATEGORY + "/" + categoryId)}
                                            onNext={(categoryId) => router.redirectTo(history, PAGE_CATEGORY + "/" + categoryId)}
                                        />}
                                /> : null}
                            {bootstrap.user.roles.words ?
                                <Route path={PAGE_DICTIONARY}>
                                    <DictionaryPage
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        words={words.entities}
                                        filters={wordFilters}
                                        onSave={words.onEntitySaved}
                                        onDelete={words.onEntityDeleted}
                                    />
                                </Route> : null}
                            {/* todo: give oauth its own role */}
                            {bootstrap.user.roles.robots ?
                                <Route path={PAGE_OAUTH}>
                                    <OAuthPage
                                        snackbar={snackbar}
                                        integrations={integrations.entities}
                                    />
                                </Route> : null}
                            {bootstrap.user.roles.users ?
                                <Route path={PAGE_AGGREGATES}>
                                    <AggregatePage
                                        dimensions={dimensions}
                                        apps={apps.entities}
                                        appRenderer={appRenderer}
                                        aggregates={aggregates.entities}
                                        filters={aggregateFilters}
                                    />
                                </Route> : null}
                            <Route path={PAGE_SETTINGS}>
                                <ProfilePage
                                    snackbar={snackbar}
                                    user={bootstrap.user}
                                    onSaveProfile={bootstrap.syncProfile}
                                    onSaveNotifications={bootstrap.syncNotifications}
                                    profileService={profileService}
                                    notificationService={notificationService}
                                    settingsForm={UserSettingsForm}
                                    showImage={true}
                                    showBirthday={true}
                                    allowApp={true}
                                    allowEmail={true}
                                    onDeleteProfile={onLogout}
                                />
                            </Route>
                            <Route path={PAGE_PASSWORD}>
                                <ChangePasswordPage
                                    snackbar={snackbar}
                                    user={bootstrap.user}
                                    authService={authService}
                                    onSaveProfile={bootstrap.syncProfile}
                                    onToHome={() => router.redirectTo(history, PAGE_HOME)}
                                />
                            </Route>
                            <Route path={PAGE_ABOUT}>
                                <AboutPage
                                    src={icon}
                                    appName={APP_NAME}
                                    appDescription={APP_TAG_LINE}
                                    appService={appService}
                                    user={bootstrap.user}
                                />
                            </Route>
                            <Route>
                                <NotFoundPage/>
                            </Route>
                        </Switch>
                    </div>
                    <Footer
                        dimensions={dimensions}
                        user={bootstrap.user}
                        onLogout={onLogout}
                    />
                </> :
                <>
                    <PublicHeader
                        dimensions={dimensions}
                        appName={APP_NAME}
                        src={icon}
                    />
                    <div className="content">
                        <Switch>
                            <Route path={PAGE_LOGIN}>
                                <LoginPage
                                    snackbar={snackbar}
                                    authService={authService}
                                    onForgotPassword={() => router.redirectTo(history, PAGE_RECOVERY)}
                                    onLogin={onLogin}
                                />
                            </Route>
                            <Route path={PAGE_RECOVERY}>
                                <ForgotPasswordPage
                                    snackbar={snackbar}
                                    authService={authService}
                                    onBackToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                    onLogin={onLogin}
                                    appName={APP_NAME}
                                />
                            </Route>
                            <Route path={PAGE_WELCOME}>
                                <WelcomePage
                                    snackbar={snackbar}
                                    authService={authService}
                                    onToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                    onLogin={onLogin}
                                />
                            </Route>
                        </Switch>
                    </div>
                </>}
    </div>
}

export default withRouter(App);
